import React, { useCallback, useEffect, useState } from 'react';
import { Building2, MessageCircle, Camera, Search, SlidersHorizontal } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import EmptyState from '../../components/ui/EmptyState';
import { getApiErrorMessage } from '../../utils/errors';
import {
  getAccountCatalogs,
  listAdminAccounts,
  updateAccountCatalogs,
} from '../../api/accounts';
import { listWhatsappCatalogAdmin } from '../../api/whatsappCatalogAdmin';
import { listInstagramAccounts } from '../../api/instagramAccounts';

export default function AdminAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal de gerenciamento de catálogos
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [allWhatsappSlots, setAllWhatsappSlots] = useState([]);
  const [allInstagramSlots, setAllInstagramSlots] = useState([]);
  const [selectedWaIds, setSelectedWaIds] = useState([]);
  const [selectedIgIds, setSelectedIgIds] = useState([]);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listAdminAccounts({ search, perPage: 50 });
      setAccounts(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar as contas.'));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleOpenCatalogsModal = async (account) => {
    setSelectedAccount(account);
    setModalOpen(true);
    setModalLoading(true);
    setModalError('');

    try {
      const [waRes, igRes, assignedRes] = await Promise.all([
        listWhatsappCatalogAdmin(),
        listInstagramAccounts(),
        getAccountCatalogs(account.id),
      ]);
      setAllWhatsappSlots(waRes.data || []);
      setAllInstagramSlots(igRes.data || []);
      setSelectedWaIds(assignedRes.whatsapp_catalog_ids || []);
      setSelectedIgIds(assignedRes.instagram_catalog_ids || []);
    } catch (err) {
      setModalError(getApiErrorMessage(err, 'Erro ao carregar catálogos da conta.'));
    } finally {
      setModalLoading(false);
    }
  };

  const handleToggleWa = (id) => {
    setSelectedWaIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleIg = (id) => {
    setSelectedIgIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSaveCatalogs = async () => {
    if (!selectedAccount) return;
    setModalSaving(true);
    setModalError('');
    try {
      await updateAccountCatalogs(selectedAccount.id, {
        whatsapp_catalog_ids: selectedWaIds,
        instagram_catalog_ids: selectedIgIds,
      });
      setSuccess(`Catálogos atualizados com sucesso para "${selectedAccount.name}".`);
      setModalOpen(false);
      loadAccounts();
    } catch (err) {
      setModalError(getApiErrorMessage(err, 'Não foi possível salvar os catálogos.'));
    } finally {
      setModalSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">
            Contas & Catálogos
          </h1>
          <p className="text-sm text-content-muted mt-1">
            Defina quais catálogos de WhatsApp e Instagram estão liberados para cada conta cliente.
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200">
          {success}
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-content-muted" />
          <input
            type="text"
            placeholder="Buscar contas pelo nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-forge-500"
          />
        </div>
        <span className="text-xs text-content-muted">
          Total: {total} conta{total !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="surface-card p-12 text-center text-content-muted">
          Carregando contas...
        </div>
      ) : accounts.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Nenhuma conta encontrada"
          description={search ? 'Tente refinar sua busca.' : 'Nenhuma conta cadastrada na plataforma.'}
        />
      ) : (
        <div className="surface-card shadow-card-md overflow-hidden rounded-xl border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-content-primary">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-content-secondary uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3">Conta / Workspace</th>
                  <th className="px-6 py-3">Modo de Execução</th>
                  <th className="px-6 py-3">WhatsApp Catálogos</th>
                  <th className="px-6 py-3">Instagram Catálogos</th>
                  <th className="px-6 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {accounts.map((acc) => (
                  <tr key={acc.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-content-primary">{acc.name}</div>
                      <div className="text-xs text-content-muted font-mono">{acc.id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                        {acc.execution_mode || 'standard'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                        acc.whatsapp_catalogs_count > 0
                          ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        <MessageCircle className="h-3 w-3" />
                        {acc.whatsapp_catalogs_count} atribuído{acc.whatsapp_catalogs_count !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                        acc.instagram_catalogs_count > 0
                          ? 'bg-fuchsia-50 text-fuchsia-800 border border-fuchsia-200'
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        <Camera className="h-3 w-3" />
                        {acc.instagram_catalogs_count} atribuído{acc.instagram_catalogs_count !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => handleOpenCatalogsModal(acc)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-forge-50 text-forge-700 border border-forge-200 hover:bg-forge-100 transition-colors"
                      >
                        <SlidersHorizontal className="h-3.5 w-3.5" />
                        Atribuir Catálogos
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de atribuição de catálogos */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Atribuir Catálogos: ${selectedAccount?.name || ''}`}
        disableClose={modalSaving}
        panelClassName="max-w-2xl max-h-[85vh] flex flex-col"
      >
        <div className="flex-1 overflow-y-auto space-y-6 my-4 pr-1">
          {modalError && (
            <div className="p-3 rounded bg-red-50 text-red-700 text-xs border border-red-200">
              {modalError}
            </div>
          )}

          {modalLoading ? (
            <div className="py-8 text-center text-sm text-content-muted">
              Carregando catálogos disponíveis...
            </div>
          ) : (
            <>
              {/* WhatsApp Slots */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-content-primary flex items-center gap-1.5">
                    <MessageCircle className="h-4 w-4 text-emerald-600" />
                    Slots de WhatsApp ({selectedWaIds.length} selecionado{selectedWaIds.length !== 1 ? 's' : ''})
                  </h3>
                </div>
                {allWhatsappSlots.length === 0 ? (
                  <p className="text-xs text-content-muted">Nenhum slot de WhatsApp cadastrado na plataforma.</p>
                ) : (
                  <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50/50 max-h-56 overflow-y-auto">
                    {allWhatsappSlots.map((slot) => {
                      const isSelected = selectedWaIds.includes(slot.id);
                      return (
                        <label
                          key={slot.id}
                          className={`flex items-start gap-3 p-2 rounded-md border text-xs cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-emerald-50/70 border-emerald-200 text-emerald-950 font-medium'
                              : 'bg-white border-slate-200 hover:bg-slate-50 text-content-secondary'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleWa(slot.id)}
                            className="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-semibold text-content-primary truncate">
                                {slot.label || slot.slug}
                              </span>
                              <span className="text-[10px] text-content-muted font-mono">{slot.slug}</span>
                            </div>
                            <div className="text-[11px] text-content-muted flex items-center gap-2 mt-0.5">
                              <span>Provedor: {slot.provider}</span>
                              {slot.phone_number && <span>· Tel: {slot.phone_number}</span>}
                              <span>· {slot.is_active ? 'Ativo' : 'Inativo'}</span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Instagram Slots */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-content-primary flex items-center gap-1.5">
                    <Camera className="h-4 w-4 text-fuchsia-600" />
                    Slots de Instagram ({selectedIgIds.length} selecionado{selectedIgIds.length !== 1 ? 's' : ''})
                  </h3>
                </div>
                {allInstagramSlots.length === 0 ? (
                  <p className="text-xs text-content-muted">Nenhum slot de Instagram cadastrado na plataforma.</p>
                ) : (
                  <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50/50 max-h-56 overflow-y-auto">
                    {allInstagramSlots.map((slot) => {
                      const isSelected = selectedIgIds.includes(slot.id);
                      return (
                        <label
                          key={slot.id}
                          className={`flex items-start gap-3 p-2 rounded-md border text-xs cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-fuchsia-50/70 border-fuchsia-200 text-fuchsia-950 font-medium'
                              : 'bg-white border-slate-200 hover:bg-slate-50 text-content-secondary'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleIg(slot.id)}
                            className="mt-0.5 rounded border-slate-300 text-fuchsia-600 focus:ring-fuchsia-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-semibold text-content-primary truncate">
                                {slot.label || slot.slug}
                              </span>
                              <span className="text-[10px] text-content-muted font-mono">{slot.slug}</span>
                            </div>
                            <div className="text-[11px] text-content-muted flex items-center gap-2 mt-0.5">
                              {slot.username && <span>@{slot.username}</span>}
                              <span>· {slot.is_active ? 'Ativo' : 'Inativo'}</span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
          <button
            type="button"
            onClick={() => setModalOpen(false)}
            disabled={modalSaving}
            className="btn-secondary text-sm"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSaveCatalogs}
            disabled={modalSaving || modalLoading}
            className="btn-primary text-sm"
          >
            {modalSaving ? 'Salvando...' : 'Salvar Atribuições'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
